import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { v4 as uuidv4 } from "uuid";

import { PlusIcon } from "../../icons/PlusIcon/PlusIcon";
import { RemoveIcon } from "../../icons/RemoveIcon/RemoveIcon";
import { creatingDocumentationActions } from "../../store/documentation-slice";

import styles from "./custom-email-input.module.css";

export type EmailProps = {
  id: string;
  email: string;
};

type CustomEmailInputProps = {
  onInputChange: (value: EmailProps) => void;
  initialValues?: EmailProps[];
  placeholder?: string;
  shareDefect?: boolean;
};

export const CustomEmailInput = ({
  onInputChange,
  placeholder,
  initialValues = [],
  shareDefect = false,
}: CustomEmailInputProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [emailInputs, setEmailInputs] = useState<EmailProps[]>([]);

  useEffect(() => {
    const initial = initialValues.map((input) => ({
      ...input,
      id: input.id || uuidv4(),
    }));

    if (initial.length === 0 && shareDefect) {
      setEmailInputs([{ id: uuidv4(), email: "" }]);
    } else {
      setEmailInputs(initial);
    }
  }, [initialValues, shareDefect]);

  const [errors, setErrors] = useState<Record<string, boolean>>({});
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleInputChange = useCallback((id: string, email: string) => {
    setEmailInputs((prevEmailInputs) =>
      prevEmailInputs.map((input) =>
        input.id === id ? { ...input, email } : input
      )
    );

    setErrors((prevErrors) => ({
      ...prevErrors,
      [id]: !emailRegex.test(email),
    }));
  }, []);

  const handleBlur = useCallback(
    (id: string) => {
      const input = emailInputs.find((input) => input.id === id);
      if (input) {
        const isValid = emailRegex.test(input.email);
        setErrors((prevErrors) => ({ ...prevErrors, [id]: !isValid }));

        if (isValid) {
          onInputChange(input);
        }
      }
    },
    [onInputChange, emailInputs]
  );

  const handleAddEmail = () => {
    if (shareDefect && emailInputs.length >= 10) {
      return;
    }

    setEmailInputs((prevEmailInputs) => [
      ...prevEmailInputs,
      { id: uuidv4(), email: "" },
    ]);
  };

  const handleDeleteEmail = (emailToDelete: EmailProps) => {
    setEmailInputs((prevEmailInputs) => {
      const updatedInputs = prevEmailInputs.filter(
        (input) => input.email !== emailToDelete.email
      );
  
      if (shareDefect && updatedInputs.length === 0) {
        return [{ id: uuidv4(), email: "" }];
      }
  
      return updatedInputs;
    });
  
    dispatch(creatingDocumentationActions.removeEmail(emailToDelete.email));
  
    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      delete newErrors[emailToDelete.id];
      return newErrors;
    });
  };
  

  return (
    <>
      <label
        className={`${styles["custom-email-input"]}  ${
          shareDefect && styles["custom-email-input__share-defect"]
        }`}
      >
        {t("Recipient")}

        <PlusIcon color="dark" handlePlusClicked={handleAddEmail} />
      </label>

      <ul
        className={`${styles["custom-email-input__email"]} ${
          shareDefect && styles["custom-email-input__email-share-defect"]
        } `}
      >
        {emailInputs.map((input) => (
          <li
            key={input.id}
            className={`${styles["custom-email-input__box"]} ${
              shareDefect && styles["custom-email-input__box-share-defect"]
            } ${errors[input.id] ? styles["error-border"] : ""}`}
          >
            <input
              type="text"
              placeholder={placeholder}
              onChange={(e) => handleInputChange(input.id, e.target.value)}
              onBlur={() => handleBlur(input.id)}
              value={input.email}
              style={{ color: errors[input.id] ? "#CB3844" : "inherit" }}
            />

            {input.email &&
              (shareDefect ? (
                <span
                  className={styles["custom-email-input__remove"]}
                  onClick={() => handleDeleteEmail(input)}
                >
                  -
                </span>
              ) : (
                <RemoveIcon
                  handleRemoveClicked={() => handleDeleteEmail(input)}
                />
              ))}
          </li>
        ))}
      </ul>

      {Object.values(errors).some((hasError) => hasError) && (
        <span className={styles["error-message"]}>
          {t("EmailInvalidMessage")}
        </span>
      )}
    </>
  );
};
